/**
 * The email thread between Kono and one vendor about one RFQ, oldest first:
 * the RFQ we sent, the follow-ups, the vendor's reply in their own words,
 * and our answer. Built from the vendor request record, the same way the
 * internal Respond drawer does it (functions/src/rfq/vendor-thread.ts), so
 * both read alike. Emails sent before the log existed keep only subject and
 * time; their entries have an empty `text`.
 */
import type { RfqVendorRequestRow } from "./api";

export type VendorThreadMessage = {
  direction: "out" | "in";
  kind: "rfq" | "follow-up" | "vendor-reply" | "our-reply";
  at: string;
  from: string;
  to: string[];
  by: string;
  subject: string;
  text: string;
};

export const vendorThreadOf = (r: RfqVendorRequestRow): VendorThreadMessage[] => {
  const out: VendorThreadMessage[] = [];
  const log = r.emailLog || [];
  const recipients = r.emails?.length ? r.emails : r.email ? [r.email] : [];

  for (const e of log) {
    out.push({ direction: "out", kind: e.kind, at: e.at, from: e.from, to: e.to, by: e.by, subject: e.subject, text: e.text || "" });
  }
  if (!log.some((e) => e.kind === "rfq") && r.sentAt) {
    out.push({ direction: "out", kind: "rfq", at: r.sentAt, from: "", to: recipients, by: "", subject: r.subject || "", text: "" });
  }
  const logged = new Set(log.map((e) => e.at));
  const manual = (r.manualFollowUps || []).filter((m) => m.at && !logged.has(m.at));
  for (const m of manual) {
    out.push({ direction: "out", kind: "follow-up", at: m.at, from: m.from || "", to: m.to?.length ? m.to : recipients, by: m.by || "", subject: m.subject || "", text: "" });
  }
  const known = log.filter((e) => e.kind === "follow-up").length + manual.length;
  const missing = Math.max(0, (r.followUpsSent || 0) - known);
  if (missing > 0 && r.lastEmailAt) {
    out.push({
      direction: "out",
      kind: "follow-up",
      at: r.lastEmailAt,
      from: "",
      to: recipients,
      by: "schedule",
      subject: r.subject ? `Re: ${r.subject}` : "",
      text: missing === 1 ? "" : `${missing} scheduled follow-ups; this is the last one.`,
    });
  }
  if (r.responseText || r.respondedAt) {
    out.push({
      direction: "in",
      kind: "vendor-reply",
      at: r.responseReceivedAt || r.respondedAt || "",
      from: r.responseFrom || r.email || recipients[0] || "",
      to: [],
      by: "",
      subject: r.subject ? `Re: ${r.subject}` : "",
      text: r.responseText || (r.declineReason ? `(declined: ${r.declineReason})` : r.responseSummary || ""),
    });
  }
  const reply = r.vendorReply || r.declineReply;
  if (reply?.at) {
    out.push({ direction: "out", kind: "our-reply", at: reply.at, from: reply.from || "", to: reply.to?.length ? reply.to : recipients, by: reply.by || "", subject: reply.subject || "", text: reply.body || "" });
  }
  return out.sort((a, b) => a.at.localeCompare(b.at));
};
